import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api, upload } from '../api/client.ts'
import { CommentsPanel } from '../components/CommentsPanel.tsx'
import { SharePanel } from '../components/SharePanel.tsx'
import { Icon } from '../components/Icon.tsx'
import { SendToChatDialog } from '../components/SendToChatDialog.tsx'
import { onServerEvent } from '../realtime/socket.ts'

/**
 * ОДИН СПИСОК ПОКУПОК
 *
 * Купленное не исчезает, а уезжает вниз зачёркнутым: в магазине полезно
 * видеть, что уже взято, а «убрать купленное» — отдельное осознанное
 * действие после похода.
 *
 * Поле добавления всегда наверху и не теряет фокус: в магазине пункты
 * добавляют очередью, по одному, и каждый раз тянуться к нему мышкой
 * невозможно.
 */

type Item = {
  id: string
  title: string
  quantity: number | null
  unit: string | null
  note: string | null
  category: string | null
  checked: boolean
  checkedBy: { id: string; displayName: string } | null
  addedBy: { id: string; displayName: string } | null
  comments: number
  photos: { id: string; thumbUrl: string | null }[]
}

type ListData = {
  list: {
    id: string
    name: string
    icon: string | null
    store: { id: string; name: string } | null
    owner: { id: string; displayName: string }
    mine: boolean
    canWrite: boolean
  }
  items: Item[]
}

/** «2 шт», «1.5 кг» — количество показываем только если оно задано. */
const amount = (item: Item): string =>
  item.quantity ? `${item.quantity.toLocaleString('ru-RU')}${item.unit ? ' ' + item.unit : ''}` : ''

export function ShoppingListPage() {
  const { listId } = useParams<{ listId: string }>()
  // Из переписки приходят по ссылке на конкретный пункт: /shopping/<список>#<пункт>
  const { hash } = useLocation()
  const [data, setData] = useState<ListData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [openItem, setOpenItem] = useState<Item | null>(null)
  const [sharing, setSharing] = useState(false)
  const [sending, setSending] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  /** Номера пунктов держим отдельно: обработчик событий живёт дольше отрисовки */
  const itemIds = useRef<Set<string>>(new Set())

  const load = () => {
    if (!listId) return
    api<ListData>(`/api/shopping/${listId}`)
      .then((fresh) => {
        itemIds.current = new Set(fresh.items.map((i) => i.id))
        setData(fresh)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось открыть список'))
  }

  useEffect(load, [listId])

  /**
   * Список общий: пока один в магазине отмечает, другой должен это видеть.
   *
   * Слушаем и сам список, и его пункты: отметить покупку могли с карточки
   * в переписке, а оттуда приходит изменение пункта, а не списка.
   */
  useEffect(() => {
    return onServerEvent((event) => {
      if (event.type !== 'card:changed') return
      if (event.entityId === listId) load()
      else if (event.entityType === 'SHOPPING_ITEM' && itemIds.current.has(event.entityId)) load()
    })
  }, [listId])

  /**
   * Пункт, на который указывает ссылка из переписки, подсвечиваем и
   * подводим к нему. Список бывает длинным, и «открыть список» без этого
   * означало бы «ищи глазами сам».
   */
  const highlight = hash.slice(1)
  useEffect(() => {
    if (!highlight || !data) return
    const row = document.getElementById(`item-${highlight}`)
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlight, data])

  const add = async () => {
    const text = title.trim()
    if (!text || !listId) return

    setTitle('')
    try {
      const created = await api<{ item: Item }>(`/api/shopping/${listId}/items`, {
        method: 'POST',
        body: { title: text },
      })
      setData((current) =>
        current ? { ...current, items: [...current.items, created.item] } : current,
      )
    } catch (e) {
      setTitle(text)
      setError(e instanceof Error ? e.message : 'Не добавилось')
    } finally {
      inputRef.current?.focus()
    }
  }

  const toggle = async (item: Item) => {
    // Отмечаем сразу, не дожидаясь сервера: в магазине важна скорость,
    // а не безупречность. Если не пройдёт — вернём обратно.
    setData((current) =>
      current
        ? {
            ...current,
            items: current.items.map((i) =>
              i.id === item.id ? { ...i, checked: !i.checked } : i,
            ),
          }
        : current,
    )

    try {
      await api(`/api/shopping/items/${item.id}`, {
        method: 'PATCH',
        body: { checked: !item.checked },
      })
    } catch {
      load()
    }
  }

  const remove = async (item: Item) => {
    setData((current) =>
      current ? { ...current, items: current.items.filter((i) => i.id !== item.id) } : current,
    )
    await api(`/api/shopping/items/${item.id}`, { method: 'DELETE' }).catch(load)
  }

  const clearDone = async () => {
    if (!listId) return
    if (!window.confirm('Убрать всё купленное из списка?')) return
    await api(`/api/shopping/${listId}/clear-done`, { method: 'POST' })
    load()
  }

  if (error) {
    return (
      <div className="section">
        <div className="center-screen">
          <p className="empty-hint">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="section">
        <div className="center-screen">
          <div className="spinner" />
        </div>
      </div>
    )
  }

  const pending = data.items.filter((i) => !i.checked)
  const done = data.items.filter((i) => i.checked)

  return (
    <div className="section">
      <header className="section-head">
        <Link to="/shopping" className="icon-btn" aria-label="Назад">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
          </svg>
        </Link>

        <div className="section-title">
          <h1>{data.list.name}</h1>
          {!data.list.mine && <span className="section-sub">список {data.list.owner.displayName}</span>}
        </div>

        <button className="icon-btn" onClick={() => setSending(true)} title="Отправить в переписку">
          <svg viewBox="0 0 24 24" width="21" height="21" fill="currentColor">
            <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
          </svg>
        </button>

        {data.list.mine && (
          <button className="icon-btn" onClick={() => setSharing(true)} title="Кому открыт">
            <Icon name="family" size={21} />
          </button>
        )}
      </header>

      {data.list.canWrite && (
        <div className="add-row">
          <input
            ref={inputRef}
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
            placeholder="Что купить?"
            maxLength={200}
          />
          <button className="btn-primary" onClick={() => void add()} disabled={!title.trim()}>
            Добавить
          </button>
        </div>
      )}

      <div className="section-body scroll">
        {data.items.length === 0 && (
          <p className="empty-hint">Список пуст. Добавь первый пункт сверху.</p>
        )}

        {pending.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            canWrite={data.list.canWrite}
            highlighted={item.id === highlight}
            onToggle={() => void toggle(item)}
            onOpen={() => setOpenItem(item)}
            onRemove={() => void remove(item)}
          />
        ))}

        {done.length > 0 && (
          <div className="done-head">
            <span>Куплено · {done.length}</span>
            {data.list.canWrite && (
              <button className="link-btn" onClick={() => void clearDone()}>
                Убрать из списка
              </button>
            )}
          </div>
        )}

        {done.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            canWrite={data.list.canWrite}
            highlighted={item.id === highlight}
            onToggle={() => void toggle(item)}
            onOpen={() => setOpenItem(item)}
            onRemove={() => void remove(item)}
          />
        ))}
      </div>

      {openItem && (
        <ItemSheet
          item={openItem}
          canWrite={data.list.canWrite}
          onClose={() => {
            setOpenItem(null)
            load()
          }}
        />
      )}

      {sending && (
        <SendToChatDialog
          entityType="SHOPPING_LIST"
          entityId={data.list.id}
          title={data.list.name}
          onClose={() => setSending(false)}
        />
      )}

      {sharing && (
        <SharePanel
          entityType="SHOPPING_LIST"
          entityId={data.list.id}
          onClose={() => setSharing(false)}
          title={`Кому открыт «${data.list.name}»`}
        />
      )}
    </div>
  )
}

function ItemRow({
  item,
  canWrite,
  highlighted,
  onToggle,
  onOpen,
  onRemove,
}: {
  item: Item
  canWrite: boolean
  highlighted: boolean
  onToggle: () => void
  onOpen: () => void
  onRemove: () => void
}) {
  return (
    <div
      id={`item-${item.id}`}
      className={
        'item-row' + (item.checked ? ' is-done' : '') + (highlighted ? ' is-highlighted' : '')
      }
    >
      <button
        className="item-check"
        onClick={onToggle}
        disabled={!canWrite}
        aria-label={item.checked ? 'Вернуть в список' : 'Куплено'}
      >
        {item.checked ? '✓' : ''}
      </button>

      <button className="item-main" onClick={onOpen}>
        <span className="item-title">
          {item.title}
          {amount(item) && <span className="item-amount">{amount(item)}</span>}
        </span>

        <span className="item-marks">
          {item.photos.length > 0 && (
            <span title="Есть фото" className="item-mark">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                   strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8a2 2 0 0 1 2-2h1.5l1-2h7l1 2H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z" />
                <circle cx="12" cy="12.5" r="3.2" />
              </svg>
            </span>
          )}
          {item.comments > 0 && (
            <span title="Есть обсуждение" className="item-mark">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                   strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 12a7.5 7.5 0 0 1-7.5 7.5H5.5L4 21v-4.2A7.5 7.5 0 1 1 20 12z" />
              </svg>
              {item.comments}
            </span>
          )}
          {item.checked && item.checkedBy && (
            <span className="item-who">{item.checkedBy.displayName}</span>
          )}
        </span>
      </button>

      {canWrite && (
        <button className="item-remove" onClick={onRemove} aria-label="Удалить пункт">
          ✕
        </button>
      )}
    </div>
  )
}

/** Подробности пункта: количество, заметка, фото и обсуждение. */
function ItemSheet({
  item,
  canWrite,
  onClose,
}: {
  item: Item
  canWrite: boolean
  onClose: () => void
}) {
  const [quantity, setQuantity] = useState(item.quantity?.toString() ?? '')
  const [unit, setUnit] = useState(item.unit ?? '')
  const [note, setNote] = useState(item.note ?? '')
  const [photos, setPhotos] = useState(item.photos)
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const save = async () => {
    setBusy(true)
    try {
      await api(`/api/shopping/items/${item.id}`, {
        method: 'PATCH',
        body: {
          quantity: quantity.trim() ? Number(quantity.replace(',', '.')) : null,
          unit: unit.trim() || null,
          note: note.trim() || null,
        },
      })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  const addPhoto = async (file: File) => {
    setBusy(true)
    try {
      const form = new FormData()
      form.append('files', file, file.name)
      const result = await upload<{ attachments: { id: string; thumbUrl: string | null }[] }>(
        `/api/attachments/SHOPPING_ITEM/${item.id}`,
        form,
      )
      setPhotos((current) => [...current, ...result.attachments])
    } finally {
      setBusy(false)
    }
  }

  const dropPhoto = async (id: string) => {
    setPhotos((current) => current.filter((p) => p.id !== id))
    await api(`/api/attachments/${id}`, { method: 'DELETE' })
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{item.title}</h2>
          <button
            className="icon-btn"
            onClick={() => setSending(true)}
            title="Отправить в переписку"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
            </svg>
          </button>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {sending && (
          <SendToChatDialog
            entityType="SHOPPING_ITEM"
            entityId={item.id}
            title={item.title}
            onClose={() => setSending(false)}
          />
        )}

        {canWrite && (
          <>
            <div className="amount-row">
              <label className="field">
                <span className="field-label">Сколько</span>
                <input
                  className="input"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="2"
                  inputMode="decimal"
                />
              </label>
              <label className="field">
                <span className="field-label">Единица</span>
                <input
                  className="input"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="шт, кг, л"
                  maxLength={16}
                />
              </label>
            </div>

            <label className="field">
              <span className="field-label">Заметка</span>
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Какой именно, где искать"
                maxLength={500}
              />
            </label>
          </>
        )}

        <h3 className="sheet-sub">Фото</h3>
        <div className="item-photos">
          {photos.map((photo) => (
            <div key={photo.id} className="item-photo">
              <img src={photo.thumbUrl ?? `/api/files/${photo.id}`} alt="" />
              {canWrite && (
                <button
                  className="item-photo-remove"
                  onClick={() => void dropPhoto(photo.id)}
                  aria-label="Убрать фото"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {canWrite && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void addPhoto(file)
                  e.target.value = ''
                }}
              />
              <button className="item-photo-add" onClick={() => fileRef.current?.click()} disabled={busy}>
                + фото
              </button>
            </>
          )}
        </div>
        <p className="sheet-note">
          Пригодится, когда просишь купить «ту самую» упаковку, а объяснить словами трудно.
        </p>

        {canWrite && item.checked && <SpendOnItem item={item} />}

        <h3 className="sheet-sub">Обсуждение</h3>
        <CommentsPanel entityType="SHOPPING_ITEM" entityId={item.id} />

        {canWrite && (
          <button className="btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? '…' : 'Сохранить'}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * СКОЛЬКО ЭТО СТОИЛО
 *
 * Появляется только у купленного: спрашивать цену у того, что ещё
 * не взяли, бессмысленно.
 *
 * Ради этого поля покупки и связаны с бюджетом. Без него пришлось бы
 * заносить одно и то же дважды — сначала отметить в списке, потом
 * вспомнить и записать в расходы. Дважды никто не заносит, и бюджет
 * остаётся пустым.
 */
function SpendOnItem({ item }: { item: Item }) {
  const [amount, setAmount] = useState('')
  const [categories, setCategories] = useState<{ id: string; name: string; icon: string | null }[]>(
    [],
  )
  const [categoryId, setCategoryId] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    api<{ categories: { id: string; name: string; icon: string | null }[] }>(
      '/api/budget/categories',
    )
      .then((data) => {
        setCategories(data.categories)
        // «Продукты» — то, что нужно в девяти случаях из десяти
        setCategoryId(
          data.categories.find((c) => c.name === 'Продукты')?.id ?? data.categories[0]?.id ?? '',
        )
      })
      .catch(() => setCategories([]))
  }, [])

  const save = async () => {
    const value = Number(amount.replace(',', '.').replace(/\s/g, ''))
    if (!Number.isFinite(value) || value <= 0) return

    setBusy(true)
    try {
      await api('/api/budget/transactions', {
        method: 'POST',
        body: {
          amount: value,
          categoryId: categoryId || null,
          description: item.title,
          shoppingItemId: item.id,
        },
      })
      setDone(true)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <>
        <h3 className="sheet-sub">Расход</h3>
        <div className="sheet-ok">Записано в бюджет</div>
      </>
    )
  }

  return (
    <>
      <h3 className="sheet-sub">Сколько стоило</h3>
      <div className="add-row" style={{ border: 'none', padding: 0 }}>
        <input
          className="input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Цена"
          inputMode="decimal"
          style={{ maxWidth: 100 }}
        />

        {categories.length > 0 && (
          <select
            className="input"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.icon} {category.name}
              </option>
            ))}
          </select>
        )}

        <button className="btn-primary" onClick={() => void save()} disabled={busy || !amount.trim()}>
          В бюджет
        </button>
      </div>
      <p className="sheet-note">Запишется расходом — второй раз заносить не придётся.</p>
    </>
  )
}
